import { createFileRoute, notFound } from "@tanstack/react-router";
import { MemoryBrowsePage } from "@/components/memory/memory-browse-page";
import type { RecordShape } from "@/components/views/views-data";
import { collectionRouteValue } from "@/lib/collection-selection";
import { isReservedOwnerSegment } from "@/lib/reserved";
import { decodeEntityTypeParam } from "@/lib/url";

const SHAPES: ReadonlySet<string> = new Set<RecordShape>([
	"table",
	"board",
	"graph",
]);

/**
 * The Records tab pinned to one shape: `/{owner}/<type>/-/table`, `/-/board`,
 * or `/-/graph` (the records and what they link to). The bare type URL renders the schema's default shape instead.
 */
export const Route = createFileRoute("/$owner/$entityType/-/$shape")({
	validateSearch: (
		search: Record<string, unknown>,
	): {
		segment?: string;
		$collection?: string;
		$preset?: string;
		$table?: string;
	} => ({
		$collection: collectionRouteValue(search.$collection),
		$preset: collectionRouteValue(search.$preset),
		$table: collectionRouteValue(search.$table),
		segment:
			typeof search.segment === "string" && search.segment
				? search.segment
				: undefined,
	}),
	beforeLoad: ({ params }) => {
		if (isReservedOwnerSegment(params.entityType)) throw notFound();
		if (!SHAPES.has(params.shape)) throw notFound();
	},
	component: EntityTypeShapePage,
});

function EntityTypeShapePage() {
	const { owner, entityType, shape } = Route.useParams();
	return (
		<MemoryBrowsePage
			ownerSlug={owner}
			typeScope={decodeEntityTypeParam(entityType)}
			create={false}
			shape={shape as RecordShape}
		/>
	);
}
